// AAL-86 ticket 18: which fields of a Service the Copilot filled. A Service carries a top-level
// `metadata` object beside `sys` and `fields` (Content Manager entry metadata, where Contentful tags
// already live); `metadata.aiGenerated` maps the contract field path without locale segments
// (`longDescription`, `ticket.ticketOptions.0.price`) to true. Pure: no Vue, no DOM.

import type { PopulatedFormContract, PopulatedField } from './form-contract'
import { readPath, type ReviewEntry } from './proposal-apply'

export type AiGenerated = Record<string, true>
export type ServiceMetadata = { aiGenerated?: AiGenerated, [key: string]: unknown }
export type AiIconTarget = { key: string, section: string, fieldKey: string, path: string }

const OPEN = [ 'proposed', 'pending' ]

/** Entity path -> aiGenerated key: every `fields.<key>.<locale>` becomes `<key>`, list indexes stay. */
export function aiGeneratedKey(path: string): string {
	const segments = path.split('.')
	const key: string[] = []
	for (let i = 0; i < segments.length;) {
		if (segments[i] === 'fields' && i + 1 < segments.length) {
			key.push(segments[i + 1])
			i += 3
		}
		else key.push(segments[i++])
	}
	return key.join('.')
}

/**
 * The Service metadata to save. Per Copilot entry of this session (a later entry for the same path
 * wins): accepted or kept proposed values that still equal the Copilot value are flagged; a revert or
 * a manual edit after the Proposal removes the flag; pending suggestions and fields the Copilot did
 * not touch keep their flag as it was. Other metadata (Contentful tags) is kept. Returns `metadata`
 * itself when there is nothing to record, so a Service without Copilot use is saved exactly as before.
 * Removes shift list indexes; flags are not re-indexed (an icon may then sit on the neighbouring item).
 */
export function metadataOnSave(metadata: ServiceMetadata | undefined, entries: ReviewEntry[], entity: unknown): ServiceMetadata | undefined {
	const decided = new Map<string, boolean>()
	for (const entry of entries) {
		if (entry.op === 'remove' || entry.status === 'pending') continue
		const applied = entry.status === 'proposed' || entry.status === 'accepted'
		decided.set(aiGeneratedKey(entry.path), applied && sameValue(readPath(entity, entry.path), entry.value))
	}
	if (!decided.size) return metadata
	const aiGenerated: AiGenerated = { ...(metadata?.aiGenerated ?? {}) }
	for (const [ key, flagged ] of decided) {
		if (flagged) aiGenerated[key] = true
		else delete aiGenerated[key]
	}
	if (!Object.keys(aiGenerated).length && metadata?.aiGenerated === undefined) return metadata
	return { ...(metadata ?? {}), aiGenerated }
}

/**
 * The fields that get the AI icon: flagged keys that resolve to a field of the contract (the field is
 * on this Service Type and, for list items, the item still exists), without the fields that show
 * Copilot accept/revert controls right now. In contract order.
 */
export function aiIconTargets(aiGenerated: AiGenerated | undefined, contract: Pick<PopulatedFormContract, 'sections'>, entries: ReviewEntry[]): AiIconTarget[] {
	if (!aiGenerated || !Object.keys(aiGenerated).length) return []
	const open = new Set(entries.filter(e => OPEN.includes(e.status)).map(e => aiGeneratedKey(e.path)))
	const targets: AiIconTarget[] = []
	const visit = (section: string, field: Pick<PopulatedField, 'key' | 'type' | 'items' | 'value'>, path: string) => {
		const key = aiGeneratedKey(path)
		if (aiGenerated[key] && !open.has(key)) targets.push({ key, section, fieldKey: field.key, path })
		if (field.type !== 'list' || !field.items || !Array.isArray(field.value)) return
		field.value.forEach((item, index) => {
			const itemPath = `${path}.${index}`
			const itemKey = aiGeneratedKey(itemPath)
			if (aiGenerated[itemKey] && !open.has(itemKey)) targets.push({ key: itemKey, section, fieldKey: field.key, path: itemPath })
			for (const sub of field.items!) visit(section, { ...sub, value: (item as any)?.[sub.key] }, `${itemPath}.${sub.path}`)
		})
	}
	for (const section of contract.sections) {
		for (const field of section.fields) visit(section.key, field, field.path)
	}
	return targets
}

function sameValue(a: unknown, b: unknown): boolean {
	return JSON.stringify(a) === JSON.stringify(b)
}
